import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { ArrowRight, Plane, TrainFront, Car, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, MaskedLines } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";
import MapEmbed from "@/components/MapEmbed";

const ICONS = { Plane, TrainFront, Car };

const DoveSiamo = () => {
  const { t } = useLang();
  usePageMeta({ title: t.seo.wherePage.title, description: t.seo.wherePage.description });
  const w = t.wherePage;

  return (
    <div data-testid="dove-siamo-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40">
        <Reveal y={20}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{w.overline}</p>
        </Reveal>
        <h1 className="font-display font-medium text-5xl md:text-7xl tracking-tight mt-4" data-testid="dove-siamo-title">
          <MaskedLines lines={[w.title]} delay={0.05} />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-2xl text-stone text-base md:text-lg leading-relaxed font-light" data-testid="dove-siamo-sub">
            {w.sub}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-14 md:mt-20" data-testid="dove-siamo-map">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden border border-linen">
            <MapEmbed title={w.mapLabel} address={w.address} />
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16 md:mt-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5 space-y-12">
          <Reveal>
            <div className="rounded-2xl border border-linen bg-card p-8">
              <h2 className="font-display text-2xl md:text-3xl">{w.addressTitle}</h2>
              <p className="mt-5 flex items-start gap-3 text-ink/80" data-testid="dove-siamo-address">
                <MapPin className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
                {w.address}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-linen bg-card p-8">
              <h2 className="font-display text-2xl md:text-3xl">{w.distancesTitle}</h2>
              <div className="flex flex-wrap gap-2.5 mt-6" data-testid="dove-siamo-distances">
                {w.distances.map((d) => (
                  <span key={d} className="rounded-full bg-sand px-4 py-2 text-sm text-ink/80">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">{w.howTitle}</h2>
          </Reveal>
          <div className="space-y-5 mt-8">
            {w.how.map((h, i) => {
              const Icon = ICONS[h.icon] || Car;
              return (
                <Reveal key={h.title} delay={i * 0.08}>
                  <div className="rounded-2xl border border-linen bg-card p-8 flex gap-6 items-start" data-testid={`how-${h.icon.toLowerCase()}`}>
                    <div className="h-12 w-12 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-terracotta" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl">{h.title}</h3>
                      <p className="mt-2.5 text-stone font-light leading-relaxed">{h.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32" data-testid="dove-siamo-cta">
        <Reveal>
          <div className="rounded-[2rem] bg-terracotta text-cream px-8 md:px-16 py-14 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight max-w-xl">{t.homeCta.title}</h2>
            <Link
              to="/prenota"
              data-testid="dove-siamo-book-button"
              className="group shrink-0 self-start md:self-auto inline-flex items-center gap-3 rounded-full bg-cream text-ink px-8 py-4 font-medium hover:bg-sand transition-colors duration-300"
            >
              {t.homeCta.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default DoveSiamo;
